import { type PayloadAction, createSlice } from "@reduxjs/toolkit"

import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import type {
  Conversation,
  Message,
  NotificationLevel,
  PendingMessage
} from "../types/messaging.types.ts"

export interface MessagingState {
  conversations: Conversation[]
  conversationsLoading: boolean
  conversationsError: boolean
  activeConversationId: string | null
  /** Message history per conversation, oldest first. */
  messagesByConversation: Record<string, Message[]>
  hasMoreByConversation: Record<string, boolean>
  messagesLoading: Record<string, boolean>
  messagesError: Record<string, boolean>
  /** Optimistic rows, keyed by their own client token. */
  pending: Record<string, PendingMessage>
  /** Composer text per conversation, so switching rooms never loses it. */
  drafts: Record<string, string>
  unreadTotal: number
  isConnected: boolean
  /** Set when the server says a conversation the user has open is no longer theirs. */
  revokedConversationId: string | null
  /**
   * Which conversations a screen currently has open. One socket serves the
   * whole app, so the screens publish what they are showing and the socket
   * controller declares it — rather than each screen opening its own.
   */
  openConversationIds: string[]
}

const initialState: MessagingState = {
  conversations: [],
  conversationsLoading: false,
  conversationsError: false,
  activeConversationId: null,
  messagesByConversation: {},
  hasMoreByConversation: {},
  messagesLoading: {},
  messagesError: {},
  pending: {},
  drafts: {},
  unreadTotal: 0,
  isConnected: false,
  revokedConversationId: null,
  openConversationIds: []
}

const byNewest = (a: Conversation, b: Conversation) =>
  new Date(b.last_message_at ?? b.created).getTime() -
  new Date(a.last_message_at ?? a.created).getTime()

/**
 * Insert or replace, keeping the list ordered and never duplicating a row.
 * A message arriving over the socket and the same message arriving in a
 * refetch are the same row; the id decides, not the arrival order.
 */
const upsertMessage = (rows: Message[], message: Message): Message[] => {
  const index = rows.findIndex((row) => row.id === message.id)
  if (index >= 0) {
    const next = rows.slice()
    next[index] = message
    return next
  }
  const next = [...rows, message]
  next.sort(
    (a, b) =>
      new Date(a.created).getTime() - new Date(b.created).getTime() ||
      a.id.localeCompare(b.id)
  )
  return next
}

const messagingSlice = createSlice({
  name: "messaging",
  initialState,
  reducers: {
    setConversationsLoading: (state, action: PayloadAction<boolean>) => {
      state.conversationsLoading = action.payload
      if (action.payload) state.conversationsError = false
    },
    setConversationsError: (state, action: PayloadAction<boolean>) => {
      state.conversationsError = action.payload
      state.conversationsLoading = false
    },
    setConversations: (state, action: PayloadAction<Conversation[]>) => {
      state.conversations = [...action.payload].sort(byNewest)
      state.conversationsLoading = false
      state.conversationsError = false
      action.payload.forEach((conversation) => {
        // A server-held draft is the starting value, never an overwrite of
        // something the user has since typed on this device.
        if (state.drafts[conversation.id] === undefined) {
          state.drafts[conversation.id] = conversation.draft
        }
      })
    },
    upsertConversation: (state, action: PayloadAction<Conversation>) => {
      const index = state.conversations.findIndex(
        (row) => row.id === action.payload.id
      )
      if (index >= 0) state.conversations[index] = action.payload
      else state.conversations.push(action.payload)
      state.conversations.sort(byNewest)
      if (state.drafts[action.payload.id] === undefined) {
        state.drafts[action.payload.id] = action.payload.draft
      }
    },
    removeConversation: (state, action: PayloadAction<string>) => {
      state.conversations = state.conversations.filter(
        (row) => row.id !== action.payload
      )
      delete state.messagesByConversation[action.payload]
      if (state.activeConversationId === action.payload) {
        state.activeConversationId = null
      }
    },
    setActiveConversation: (state, action: PayloadAction<string | null>) => {
      state.activeConversationId = action.payload
      state.revokedConversationId = null
    },
    setMessagesLoading: (
      state,
      action: PayloadAction<{ conversationId: string; loading: boolean }>
    ) => {
      state.messagesLoading[action.payload.conversationId] =
        action.payload.loading
      if (action.payload.loading) {
        state.messagesError[action.payload.conversationId] = false
      }
    },
    setMessagesError: (
      state,
      action: PayloadAction<{ conversationId: string; error: boolean }>
    ) => {
      state.messagesError[action.payload.conversationId] = action.payload.error
      state.messagesLoading[action.payload.conversationId] = false
    },
    setMessages: (
      state,
      action: PayloadAction<{
        conversationId: string
        messages: Message[]
        hasMore: boolean
      }>
    ) => {
      state.messagesByConversation[action.payload.conversationId] =
        action.payload.messages
      state.hasMoreByConversation[action.payload.conversationId] =
        action.payload.hasMore
      state.messagesLoading[action.payload.conversationId] = false
    },
    prependMessages: (
      state,
      action: PayloadAction<{
        conversationId: string
        messages: Message[]
        hasMore: boolean
      }>
    ) => {
      const existing =
        state.messagesByConversation[action.payload.conversationId] ?? []
      const known = new Set(existing.map((row) => row.id))
      state.messagesByConversation[action.payload.conversationId] = [
        ...action.payload.messages.filter((row) => !known.has(row.id)),
        ...existing
      ]
      state.hasMoreByConversation[action.payload.conversationId] =
        action.payload.hasMore
    },
    messageArrived: (state, action: PayloadAction<Message>) => {
      const rows = state.messagesByConversation[action.payload.conversation]
      // A conversation whose history has never been fetched has no list to
      // append to — dropping the row here is correct, because opening it will
      // fetch the message along with everything else.
      if (rows) {
        state.messagesByConversation[action.payload.conversation] =
          upsertMessage(rows, action.payload)
      }
      if (action.payload.client_token) {
        delete state.pending[action.payload.client_token]
      }
      const conversation = state.conversations.find(
        (row) => row.id === action.payload.conversation
      )
      if (conversation) {
        conversation.last_message_at = action.payload.created
        conversation.last_message = {
          id: action.payload.id,
          sender_name: action.payload.sender?.full_name ?? "",
          body: action.payload.body.slice(0, 140),
          is_deleted: action.payload.is_deleted,
          created: action.payload.created
        }
        state.conversations.sort(byNewest)
      }
    },
    messageChanged: (state, action: PayloadAction<Message>) => {
      const rows = state.messagesByConversation[action.payload.conversation]
      if (rows) {
        state.messagesByConversation[action.payload.conversation] =
          upsertMessage(rows, action.payload)
        // A quoted preview holds a copy, so an edit or a deletion has to reach
        // every reply that quotes the changed message, not only the message.
        state.messagesByConversation[action.payload.conversation] =
          state.messagesByConversation[action.payload.conversation].map((row) =>
            row.reply_to?.id === action.payload.id
              ? {
                  ...row,
                  reply_to: {
                    ...row.reply_to,
                    body: action.payload.is_deleted ? "" : action.payload.body,
                    is_deleted: action.payload.is_deleted
                  }
                }
              : row
          )
      }
      const conversation = state.conversations.find(
        (row) => row.id === action.payload.conversation
      )
      if (conversation?.last_message?.id === action.payload.id) {
        conversation.last_message = {
          ...conversation.last_message,
          body: action.payload.is_deleted
            ? ""
            : action.payload.body.slice(0, 140),
          is_deleted: action.payload.is_deleted
        }
      }
    },
    messageQueued: (state, action: PayloadAction<PendingMessage>) => {
      state.pending[action.payload.client_token] = action.payload
    },
    messageFailed: (state, action: PayloadAction<string>) => {
      const row = state.pending[action.payload]
      if (row) row.status = "failed"
    },
    messageRetrying: (state, action: PayloadAction<string>) => {
      const row = state.pending[action.payload]
      if (row) row.status = "sending"
    },
    messageDiscarded: (state, action: PayloadAction<string>) => {
      delete state.pending[action.payload]
    },
    setUnreadCount: (
      state,
      action: PayloadAction<{ conversationId: string; count: number }>
    ) => {
      const conversation = state.conversations.find(
        (row) => row.id === action.payload.conversationId
      )
      if (conversation) conversation.unread_count = action.payload.count
    },
    setUnreadTotal: (state, action: PayloadAction<number>) => {
      state.unreadTotal = action.payload
    },
    setUnreadCounts: (state, action: PayloadAction<Record<string, number>>) => {
      state.conversations.forEach((conversation) => {
        conversation.unread_count = action.payload[conversation.id] ?? 0
      })
    },
    setNotificationLevelFor: (
      state,
      action: PayloadAction<{ conversationId: string; level: NotificationLevel }>
    ) => {
      const conversation = state.conversations.find(
        (row) => row.id === action.payload.conversationId
      )
      if (conversation) conversation.notification_level = action.payload.level
    },
    setDraft: (
      state,
      action: PayloadAction<{ conversationId: string; draft: string }>
    ) => {
      state.drafts[action.payload.conversationId] = action.payload.draft
    },
    setConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload
    },
    accessRevoked: (state, action: PayloadAction<string>) => {
      state.revokedConversationId = action.payload
      state.conversations = state.conversations.filter(
        (row) => row.id !== action.payload
      )
      delete state.messagesByConversation[action.payload]
    },
    clearRevoked: (state) => {
      state.revokedConversationId = null
    },
    setOpenConversations: (state, action: PayloadAction<string[]>) => {
      state.openConversationIds = action.payload
    }
  },
  extraReducers: (builder) => {
    // Drafts are per-account, and the next person to sign in on this browser
    // must not inherit them.
    builder.addCase(logout.fulfilled, () => initialState)
    builder.addCase(logout.rejected, () => initialState)
  }
})

export const {
  setConversationsLoading,
  setConversationsError,
  setConversations,
  upsertConversation,
  removeConversation,
  setActiveConversation,
  setMessagesLoading,
  setMessagesError,
  setMessages,
  prependMessages,
  messageArrived,
  messageChanged,
  messageQueued,
  messageFailed,
  messageRetrying,
  messageDiscarded,
  setUnreadCount,
  setUnreadTotal,
  setUnreadCounts,
  setNotificationLevelFor,
  setDraft,
  setConnected,
  accessRevoked,
  clearRevoked,
  setOpenConversations
} = messagingSlice.actions

export default messagingSlice
